import React, { useMemo } from "react";
import { useLocation } from "wouter";
import { useMediaQuery } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SavingsIcon from "@mui/icons-material/Savings";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";

import Button, { ButtonTypes } from "../../../../../components/Button";
import { cashbackBuysHrefOf } from "../../../../../components/CashbackSection";
import ActionsDropdown from "../../../../../components/Dropdown/ActionsDropdown";
import { DropdownAction } from "../../../../../components/Dropdown/types";
import { FocusableTooltip } from "../../../../../components/FocusableTooltip";
import { DateFormat, formatDate, formatCurrency } from "../../../../../utils";
import { QUERIES } from "../../../../../utils/constants";
import {
  canAddCashbackToBalance,
  isCashbackInUse,
} from "../../../../../utils/cashback";
import { headlineInterestOf } from "../../../utils";
import { useSavingCashback } from "../../hooks";
import { AccountDetails } from "./AccountDetails";
import { CashbackSideFigure } from "./CashbackSideFigure";
import { ValueComposition } from "./ValueComposition";
import { cashbackSideOf } from "./utils";
import { SavingHeroProps } from "./types";
import {
  HeroCard,
  HeroHead,
  HeroIdentity,
  IdentityRow,
  HeroName,
  HeroStatusBadge,
  HeroSubtitle,
  HeaderActions,
  HeroMain,
  ValuePair,
  ValueBlock,
  ValueLabel,
  ValueAmount,
  GainChip,
} from "./styles";

const GAIN_TOOLTIP = "Gross interest, before income tax.";

const SavingHeroComponent: React.FC<SavingHeroProps> = ({
  saving,
  figures,
  counts,
  hasTransactions,
  lastUpdated,
  dropdownActions,
  onAddProgress,
  onAddCashback,
  addCashbackDisabledReason,
}) => {
  const [, setLocation] = useLocation();
  const isMobile = useMediaQuery(QUERIES.tabletLargeAndDown, { noSsr: true });
  const cashback = useSavingCashback(saving.accountId, {
    skip: !hasTransactions,
  });

  const isDeactivated = !saving.isActive;
  const statusText = saving.isActive ? "Active" : "Deactivated";

  let subtitleText = "Not yet updated";
  if (isDeactivated && saving.deactivationDate) {
    subtitleText = `Deactivated on ${formatDate(saving.deactivationDate, DateFormat.DMY_SPACE)}`;
  } else if (lastUpdated) {
    subtitleText = `Updated ${formatDate(lastUpdated, DateFormat.DMY_SPACE)}`;
  }

  const isNegativeValue = saving.currentValue < 0;
  const isLoss = saving.earnedValue < 0;
  const gainValue = headlineInterestOf(saving);
  const gainText = `${isLoss ? "-" : "+"}${formatCurrency(Math.abs(gainValue))} interest`;
  const plainGainChip = (
    <GainChip $negative={isLoss} data-testid="saving-gain">
      {isLoss ? <TrendingDownIcon /> : <TrendingUpIcon />}
      {gainText}
    </GainChip>
  );
  const gainChip =
    saving.grossInterest === null ? (
      plainGainChip
    ) : (
      <FocusableTooltip title={GAIN_TOOLTIP}>{plainGainChip}</FocusableTooltip>
    );

  const { accountId, cashbackMode } = saving;
  const { summary, isError: isCashbackError } = cashback;
  const cashbackSide = summary ? cashbackSideOf(summary, cashbackMode) : null;
  const cashbackFigure =
    summary && cashbackSide ? { side: cashbackSide, summary } : null;
  const hasSideFigure =
    hasTransactions && (isCashbackError || cashbackFigure !== null);
  const isDisclosureOpen = !isMobile;

  const heroActions = useMemo<DropdownAction[]>(() => {
    if (
      isCashbackError ||
      !summary ||
      !isCashbackInUse({ ...summary, mode: cashbackMode })
    ) {
      return dropdownActions;
    }
    const cashbackActions: DropdownAction[] = [
      {
        id: "addCashback",
        label: "Add cashback",
        icon: <SavingsIcon fontSize="small" />,
        onClick: onAddCashback,
        show: canAddCashbackToBalance({ ...summary, mode: cashbackMode }),
        disabled: addCashbackDisabledReason !== null,
        disabledReason: addCashbackDisabledReason ?? undefined,
      },
      {
        id: "viewCashbackBuys",
        label: "View cashback buys",
        icon: <ReceiptLongIcon fontSize="small" />,
        onClick: () => setLocation(cashbackBuysHrefOf(accountId)),
        show: summary.invested > 0,
      },
    ];
    return [...cashbackActions, ...dropdownActions];
  }, [
    isCashbackError,
    summary,
    cashbackMode,
    accountId,
    dropdownActions,
    onAddCashback,
    addCashbackDisabledReason,
    setLocation,
  ]);

  return (
    <HeroCard elevation={0} $isDeactivated={isDeactivated}>
      <HeroHead>
        <HeroIdentity>
          <IdentityRow>
            <HeroName>{saving.name}</HeroName>
            <HeroStatusBadge $isActive={saving.isActive}>
              {statusText}
            </HeroStatusBadge>
          </IdentityRow>
          <HeroSubtitle>{subtitleText}</HeroSubtitle>
        </HeroIdentity>
        <HeaderActions>
          <ActionsDropdown
            dropdownActions={heroActions}
            testId="saving-hero-actions"
          />
          <Button
            type={ButtonTypes.primary}
            buttonType="button"
            onClick={onAddProgress}
            aria-label="Add saving progress"
          >
            <AddIcon fontSize="small" />
            Add progress
          </Button>
        </HeaderActions>
      </HeroHead>

      <HeroMain $isSingle={!hasTransactions}>
        <ValuePair $isSingle={!hasSideFigure}>
          <ValueBlock>
            <ValueLabel>Current value</ValueLabel>
            <ValueAmount $negative={isNegativeValue} data-testid="saving-value">
              {formatCurrency(saving.currentValue)}
            </ValueAmount>
            {hasTransactions && gainChip}
          </ValueBlock>
          {hasTransactions && (
            <CashbackSideFigure
              figure={cashbackFigure}
              mode={cashbackMode}
              isError={isCashbackError}
              onRetry={cashback.retry}
            />
          )}
        </ValuePair>

        {hasTransactions && (
          <ValueComposition
            netContributed={figures.netContributed}
            retainedInterest={figures.retainedInterest}
            retainedCashback={figures.retainedCashback}
            interestWithdrawn={figures.interestWithdrawn}
            cashbackWithdrawn={figures.cashbackWithdrawn}
            interestEarned={saving.earnedValue}
            taxWithheld={saving.taxWithheld}
            grossInterest={saving.grossInterest}
            currentValue={saving.currentValue}
            isInterestOpenByDefault={isDisclosureOpen}
          />
        )}
      </HeroMain>

      <AccountDetails
        saving={saving}
        counts={counts}
        hasTransactions={hasTransactions}
        isOpenByDefault={isDisclosureOpen}
      />
    </HeroCard>
  );
};

export const SavingHero = React.memo(SavingHeroComponent);
